import style from "./InputImage.module.scss";
import {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { clone, getFilePath, createImageFileFromDom } from "shared/utils/Utils";
import { adminFileRequester } from "App";
import _ from "lodash";
import clsx from "classnames";
import VerticalFlex from "layouts/flex/VerticalFlex";
import FlexChild from "layouts/flex/FlexChild";
import HorizontalFlex from "layouts/flex/HorizontalFlex";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { compressImage } from "shared/utils/Utils";
import { ColorExtractor } from "react-color-extractor";
import useAltEffect from "shared/hooks/useAltEffect";
import Loader from "./Loader";
import EasyCrop from "./EasyCrop";

const AdminImageBlock = forwardRef((props, ref) => {
  const input = useRef();
  const [name, setName] = useState(props.name || "");
  const [imageRef, setImageRef] = useState();
  const [isChanged, setChanged] = useState(false);
  const [isFileChanged, setFileChanged] = useState(false);
  const [isCropAreaChanged, setCropAreaChanged] = useState(false);
  const [isEmpty, setEmpty] = useState();
  const [value, setValue] = useState(null);
  const [colors, setColors] = useState([]);

  const uploadFile = async () => {
    let file = await createImageFileFromDom(imageRef);
    if (isChanged && typeof file === "object") {
      const formData = new FormData();
      formData.append("files", file);
      formData.append("path", props.path);
      let uploadResult = await adminFileRequester.upload(formData);
      if (uploadResult?.uploads?.[0]?.url) {
        setValue(uploadResult.uploads[0].url);
      }
    }
    return true;
  };

  const imageCallback = (imageDiv) => {
    setImageRef(imageDiv);
  };

  const onFileChange = (file) => {
    if (file) {
      setEmpty(false);
    } else {
      setEmpty(true);
    }

    let isEqual = _.isEqual(file, props.value);
    if (isEqual) {
      setFileChanged(false);
    } else {
      setFileChanged(true);
    }
  };

  const onCropAreaChange = (isCropAreaChanged) => {
    setCropAreaChanged(isCropAreaChanged);
  };

  const onChangeColors = (colors) => {
    setColors(colors);
    if (props.onChangeColors) {
      props.onChangeColors(colors);
    }
  };

  useImperativeHandle(ref, () => ({
    getName() {
      return name;
    },
    getValue() {
      return value;
    },
    getColors() {
      return colors;
    },
    async upload(callback) {
      let result = await uploadFile();
      callback(result);
    },
    isChanged() {
      return isChanged;
    },
    async isValid() {
      return await uploadFile();
    },
    focus() {
      input.current.scrollTo(0, input.current.offsetTop);
    },
  }));

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  useEffect(() => {
    setChanged(isFileChanged || isCropAreaChanged);
  }, [isFileChanged, isCropAreaChanged]);

  return (
    <div ref={input} className={style.wrapper}>
      <FileUpload
        multiple={true}
        name="example-upload"
        maxSize={300000}
        label={props.label}
        labelWidth={props.labelWidth}
        cropWidth={props.cropWidth}
        cropHeight={props.cropHeight}
        callback={imageCallback}
        value={value}
        onChange={onFileChange}
        onChangeColors={onChangeColors}
        onCropAreaChange={onCropAreaChange}
        maxSizeMb={props.maxSizeMb}
      />
    </div>
  );
});

function FileUpload(props) {
  const [file, setFile] = useState();
  const [fileName, setFileName] = useState();
  const [hoverState, setHoverState] = useState(false);
  const inputRef = useRef();
  const [src, setSrc] = useState();
  const [colors, setColors] = useState([]);
  const [isWide, setWide] = useState();

  useEffect(() => {
    setFile(props.value);
  }, [props.value]);

  const handleDragOver = (e) => {
    if ("preventDefault" in e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (e.type === "dragover") {
      setHoverState(true);
    } else {
      setHoverState(false);
    }
  };
  const handleFileSelect = (e) => {
    handleDragOver(e);
    const files = e.target.files || e.dataTransfer.files;

    for (var image of files) {
      var reader = new FileReader();

      reader.onload = function(event) {
        setSrc(event.target.result);

        var img = new Image();
        img.src = event.target.result;

        img.onload = function() {
          var w = this.width;
          var h = this.height;
          setWide(w > h);
        };
      };

      reader.readAsDataURL(image);
    }

    Object.keys(files).map((file) => {
      let fileName = files[file].name;
      compressImage(files[file], fileName, props.maxSizeMb).then((result) => {
        e.target.value = null;
        setFile(result);
      });
    });
  };

  const removeFile = () => {
    setFile(null);
  };

  const selectFile = (e) => {
    e.preventDefault();
    inputRef.current.click(e);
  };

  const getColors = (colors) => {
    setColors(colors);
  };

  useEffect(() => {
    if (file) {
      if (file.name) {
        setFileName(file.name);
      } else {
        setFileName(file);
      }
    } else {
      setFileName("No file chosen");
    }
    props.onChange(file);
  }, [file]);

  useEffect(() => {
    props.onChangeColors(colors);
  }, [colors]);

  return (
    <>
      {src ? <ColorExtractor src={src} getColors={getColors} /> : null}

      <HorizontalFlex>
        {props.label && props.labelWidth && (
          <FlexChild width={props.labelWidth}>
            <input
              type="hidden"
              name={`${props.name}:maxSize`}
              value={props.maxSize}
            />
            <span>{props.label}</span>
          </FlexChild>
        )}

        <FlexChild>
          <VerticalFlex>
            <FlexChild>
              <label>
                <div
                  className={clsx(style.fileDrag, {
                    [style.hover]: hoverState,
                  })}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragOver}
                  onDrop={handleFileSelect}
                  style={{ width: 100, height: 100 }}
                >
                  <div className={file ? null : style.inputWrapper}>
                    <input
                      type="file"
                      tabIndex="-1"
                      className={style.input}
                      name={props.name}
                      onChange={handleFileSelect}
                      ref={inputRef}
                    />
                    {file ? (
                      <div className={style.previews}>
                        <FilePreview
                          data={file}
                          onRemove={removeFile}
                          cropWidth={props.cropWidth}
                          cropHeight={props.cropHeight}
                          callback={props.callback}
                          onCropAreaChange={props.onCropAreaChange}
                          isWide={isWide}
                        />
                      </div>
                    ) : (
                      <div className={style.inputCover}></div>
                    )}
                  </div>
                </div>
              </label>
            </FlexChild>
          </VerticalFlex>
        </FlexChild>
      </HorizontalFlex>
    </>
  );
}

function FilePreview(props) {
  const [name, setName] = useState();
  const [isLoading, setLoading] = useState(true);
  const [src, setSrc] = useState();
  const [type, setType] = useState();

  useAltEffect(() => {
    if (typeof props.data === "string") {
      loadData(props.data);
    } else {
      loadFile(props.data);
    }
  }, [props.data]);

  const loadFile = (file) => {
    const reader = new FileReader();
    let dataType = file.type;
    let fileName = file.name;
    if (file.type.match("text")) {
      dataType = "text";
    } else if (file.type.match("image")) {
      dataType = "image";
    }

    if (dataType === "text") {
      reader.readAsText(file);
    } else if (dataType === "image") {
      reader.readAsDataURL(file);
    }

    reader.onload = (e) => {
      setSrc(e.target.result);
      setType(dataType);
      setLoading(false);
      setName(fileName);
    };
  };

  const loadData = (data) => {
    let dataType = "image";
    setSrc(data);
    setType(dataType);
    setLoading(false);
    setName(data);
  };

  return (
    <div
      className={clsx(style.previewItem, {
        [style.disabled]: isLoading ? true : false,
      })}
    >
      {isLoading ? (
        <Loader />
      ) : type === "image" ? (
        <VerticalFlex>
          <FlexChild height={100}>
            <HorizontalFlex>
              <FlexChild width={100}>
                <img className={style.previewImage} alt="preview" src={src} />
              </FlexChild>
              <FlexChild>
                <button
                  id="remove"
                  className={style.button}
                  onClick={props.onRemove}
                >
                  remove
                </button>
              </FlexChild>
            </HorizontalFlex>
          </FlexChild>
          <FlexChild>
            <EasyCrop
              image={src}
              cropWidth={props.cropWidth}
              cropHeight={props.cropHeight}
              callback={props.callback}
              onCropAreaChange={props.onCropAreaChange}
              isWide={props.isWide}
            />
          </FlexChild>
        </VerticalFlex>
      ) : type === "text" ? (
        <pre className={style.preview}>{src}</pre>
      ) : (
        <pre className={style.preview}>no preview</pre>
      )}
    </div>
  );
}

export default AdminImageBlock;
